import { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ContentProvider } from './context/ContentContext';
import { Layout } from './components/Layout';
import { AnalyticsConsentBanner } from './components/AnalyticsConsentBanner';
import { HomePage } from './pages/HomePage';
import { ExplorePage } from './pages/ExplorePage';
import { HighLevelIssuePage } from './pages/HighLevelIssuePage';
import { SpecificIssuePage } from './pages/SpecificIssuePage';
import { TopicPage } from './pages/TopicPage';
import { SearchPage } from './pages/SearchPage';
import { VotePage } from './pages/VotePage';
import { ClarityPage } from './pages/ClarityPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { appConfig } from './config/appConfig';

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const previous = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';

      return () => {
        window.history.scrollRestoration = previous;
      };
    }
  }, []);

  useLayoutEffect(() => {
    if (hash) {
      requestAnimationFrame(() => {
        document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView();
      });
      return;
    }

    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollToTop();
    const frame = requestAnimationFrame(scrollToTop);

    return () => cancelAnimationFrame(frame);
  }, [pathname, search, hash]);

  return null;
}

export default function App() {
  return (
    <ContentProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="explore" element={<ExplorePage />} />
            <Route path="vote" element={<VotePage />} />
            <Route path="clarity" element={<ClarityPage />} />
            <Route path="issues/:hlSlug" element={<HighLevelIssuePage />} />
            <Route path="issues/:hlSlug/:specificSlug" element={<SpecificIssuePage />} />
            <Route
              path="issues/:hlSlug/:specificSlug/:itemSlug"
              element={<TopicPage />}
            />
            <Route path="topic/:itemSlug" element={<TopicPage />} />
            <Route
              path="search"
              element={
                appConfig.enableSearch ? <SearchPage /> : <Navigate to="/" replace />
              }
            />
            {/* Legacy redirects */}
            <Route path="section/*" element={<Navigate to="/explore" replace />} />
            <Route path="source/*" element={<Navigate to="/explore" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        <AnalyticsConsentBanner />
      </BrowserRouter>
    </ContentProvider>
  );
}
